import { html, nothing } from "lit";
import type { ChatProps } from "./chat-view-props.ts";

export const FLEET_HEARTBEAT_TTL_MS = 30_000;

export function operatorTrustActivityFields(props: ChatProps) {
  const trust = props.operatorTrustSession;
  return {
    runTimeline: trust?.timeline ?? [],
    sessionHealth: trust?.sessionHealth?.health ?? null,
    sessionHealthReason: trust?.sessionHealth?.reason ?? null,
    turnPhase: trust?.turnPhase?.to ?? null,
  };
}

export function renderOperatorTrustBanner(props: ChatProps) {
  const health = props.operatorTrustSession?.sessionHealth?.health;
  if (!health || health === "healthy") {
    return nothing;
  }
  const reason = props.operatorTrustSession?.sessionHealth?.reason?.trim();
  const label =
    health === "wedged"
      ? reason || "Session wedged — try Abort or Reset Node"
      : reason || "Session slow — still working";
  return html`
    <div class="chat-operator-trust-banner chat-operator-trust-banner--${health}" role="status">
      ${label}
    </div>
  `;
}

/**
 * JAISIU-3320 / Pillar I — fixed banner shown at the top of the chat
 * pane when the operator has navigated away from the session that
 * received the most recent ≥3-child fan-out. Clicking "Return" jumps
 * back to that session; clicking the dismiss button just hides the
 * banner without changing the active session.
 */
export function renderFleetPlanActivityBanner(props: ChatProps) {
  const activity = props.fleetSessionActivity;
  const running = activity?.runningUnits ?? [];
  const planned = activity?.plannedUnits ?? [];
  if (running.length === 0 && planned.length === 0) {
    return nothing;
  }
  const total = running.length + planned.length;
  const label =
    running.length > 0
      ? `Plan active · ${running.length}/${total} units running`
      : `Plan queued · ${planned.length} units planned`;
  const firstChild = running[0]?.childSessionKey?.trim();
  const handleActivate = () => {
    if (firstChild) {
      props.onActivateFleetPlanUnit?.(firstChild);
    }
  };
  return html`
    <div
      class="chat-fleet-plan-activity-banner"
      role="status"
      aria-live="polite"
      data-testid="chat-fleet-plan-activity-banner"
    >
      <span class="chat-fleet-plan-activity-banner__label">${label}</span>
      ${
        firstChild
          ? html`<button
              type="button"
              class="chat-fleet-plan-activity-banner__jump"
              @click=${handleActivate}
            >
              Open unit
            </button>`
          : nothing
      }
    </div>
  `;
}

export function renderPinnedActiveJobBanner(props: ChatProps) {
  const pinned = (props.pinnedActiveJob ?? "").trim();
  const active = (props.sessionKey ?? "").trim();
  if (!pinned || !active || pinned === active) {
    return nothing;
  }
  if (!props.onActivatePinnedActiveJob) {
    return nothing;
  }
  const label = (props.pinnedActiveJobLabel ?? "").trim() || pinned;
  const status = (props.pinnedActiveJobStatus ?? "").trim() || "running";
  const handleActivate = () => {
    props.onActivatePinnedActiveJob?.();
  };
  const handleDismiss = () => {
    props.onDismissPinnedActiveJob?.();
  };
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleActivate();
    } else if (event.key === "Escape") {
      event.preventDefault();
      handleDismiss();
    }
  };
  return html`
    <div
      class="chat-pinned-active-job-banner"
      role="status"
      aria-live="polite"
      data-testid="chat-pinned-active-job-banner"
      tabindex="0"
      @keydown=${handleKeyDown}
    >
      <span class="chat-pinned-active-job-banner__icon" aria-hidden="true">⚡</span>
      <button
        type="button"
        class="chat-pinned-active-job-banner__main"
        title="Switch to the active job"
        @click=${handleActivate}
      >
        <span class="chat-pinned-active-job-banner__label">Active job: ${label}</span>
        <span class="chat-pinned-active-job-banner__status">· ${status}</span>
        <span class="chat-pinned-active-job-banner__hint">· click to return</span>
      </button>
      <button
        type="button"
        class="chat-pinned-active-job-banner__dismiss"
        aria-label="Dismiss pinned active job banner"
        title="Dismiss"
        @click=${handleDismiss}
      >
        ×
      </button>
    </div>
  `;
}
